import Cookies from 'js-cookie';
import env from 'env';

export interface ChatMessage {
    role: 'user' | 'assistant';
    content: string;
    charts?: ChartSpec[];
}

export interface ToolStatus {
    tool: string;
    status: 'calling' | 'done';
}

export interface ChartDataset {
    label?: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string;
}

export interface ChartSpec {
    type: 'bar' | 'line' | 'doughnut';
    title: string;
    labels: string[];
    datasets: ChartDataset[];
}

const baseUrl = new URL(`${env.API_DOMAIN}${env.API_PATH}`);

export async function streamChat(
    spotUuid: string,
    messages: ChatMessage[],
    onToken: (token: string) => void,
    onDone: () => void,
    onError: (error: string) => void,
    onToolStatus?: (toolStatus: ToolStatus) => void,
    onChart?: (chart: ChartSpec) => void,
    signal?: AbortSignal
): Promise<void> {
    const token = Cookies.get('airgarage_auth') || '';
    const url = new URL(`spots/${spotUuid}/chat/`, baseUrl);

    const response = await fetch(url.toString(), {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Token ${token}` } : {}),
        },
        body: JSON.stringify({ messages }),
        signal,
    });

    if (!response.ok) {
        onError(`Request failed with status ${response.status}`);
        return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
        onError('No response body');
        return;
    }

    const decoder = new TextDecoder();
    let buffer = '';

    for (;;) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const data = line.slice(6);
            if (data === '[DONE]') {
                onDone();
                return;
            }
            if (data === '[ERROR]') {
                onError('An error occurred while generating a response.');
                return;
            }
            try {
                const parsed = JSON.parse(data);
                if (typeof parsed === 'object' && parsed !== null) {
                    if ('tool' in parsed) {
                        onToolStatus?.(parsed as ToolStatus);
                    } else if ('chart' in parsed) {
                        onChart?.(parsed.chart as ChartSpec);
                    } else {
                        onToken(parsed);
                    }
                } else {
                    onToken(parsed);
                }
            } catch {
                onToken(data);
            }
        }
    }

    onDone();
}
